import { ICompileService, IScope } from "angular";
import { IFieldConfigurationObject } from "AngularFormly";
import { RecordDataSource } from "../../../../records/grids";
import {
  CellDoubleClickedEventHandlerArgs,
  DoubleClickHandlerService,
  PatchFieldConfiguration,
  vsName as DoubleClickHandlerServiceName,
} from "../../../../records/grids/cells/doubleClickHandlerService";
import { FilterProvider } from "../../../../records/grids/columns/filters/FilterProvider";
import { OptionsFilter } from "../../../../records/grids/columns/filters/subFilters/OptionsFilter";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../../../../records/grids/settings/gridSettingsService";
import {
  moneyValueGetterFactory,
  percentageValueGetterFactory,
} from "../../../../records/grids/cells/valueGetters";
import { IQuery } from "../../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import { meterReadingFormatter as meterReadingFormatterFactory } from "../../../grids/cellRenderer/meterReadingRenderer";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../../records/grids/columns/columnDefinitionService";
import { ColDef } from "../../../../records/grids/columns/colDef";
import { ProgressivePermissions } from "../../../permissions";
import { GridArgs } from "../../../../records/grids/gridArgs";

const gridId = Symbol("progressiveDailyReadingsGameLevel");
const progressiveDailyReadingGameLevelUrl =
  "/api/machines/progressives/ProgressiveDailyReading/GameLevel";
const progressiveDailyReadingGameLevelReadingUrl =
  "/api/machines/progressives/ProgressiveDailyReading/GameLevel/Reading";

export class DailyReadingsReadingsViewController {
  static $inject = [
    "date",
    "$scope",
    GridSettingsServiceName,
    "$compile",
    DoubleClickHandlerServiceName,
    ServerUtilityName,
    ColumnDefinitionServiceName,
  ];
  static readonly meterReadingFormatter = meterReadingFormatterFactory({
    meterReadingField: "LastMeterReading",
    conditionNotMetField: "LastConditionNotMet",
    unableToGetReadingField: "LastUnableToGetReading",
  });

  readonly gridDataSource: RecordDataSource;
  readonly gridSettings: IGridSettings;
  readonly columns: ColDef[];
  defaultQuery: IQuery;

  constructor(
    date: Date,
    $scope: IScope,
    gridSettingsService: GridSettingsService,
    $compile: ICompileService,
    private readonly doubleClickHandlerService: DoubleClickHandlerService,
    private readonly serverUtility: ServerUtility,
    columnDefinitionService: ColumnDefinitionService
  ) {
    this.defaultQuery = {
      ProgressiveDailyPeriodDate: date,
    };
    $scope.$on("dateChanged", (event, newDate) => {
      this.defaultQuery = {
        ProgressiveDailyPeriodDate: newDate,
      };
    });

    this.gridDataSource = new RecordDataSource(
      serverUtility,
      progressiveDailyReadingGameLevelUrl,
      true
    );
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    const moneyValueGetter = moneyValueGetterFactory();
    this.columns = [
      columnDefinitionService.getNumberColDef({ field: "Id", hide: true }),
      {
        field: "LastMeterReading",
        headerName: "Last Reading",
        valueGetter: (params: { data: any; colDef: ColDef }) => {
          const data = <any>params.data;
          return DailyReadingsReadingsViewController.meterReadingFormatter({
            cellValue: data[params.colDef!.field!],
            row: params.data,
          });
        },
        filter: new FilterProvider("LastMeterReading", $compile)
          .defaultNumberFilters()
          .addFilter(
            "Unable To Get Reading",
            new OptionsFilter({
              displayName: "Unable To Get Reading",
              options: [
                { display: "Yes", value: true },
                { display: "No", value: false },
              ],
              queryFieldNames: ["LastUnableToGetReading"],
            })
          )
          .addFilter(
            "Condition Not Met",
            new OptionsFilter({
              displayName: "Condition Not Met",
              options: [
                { display: "Yes", value: true },
                { display: "No", value: false },
              ],
              queryFieldNames: ["LastConditionNotMet"],
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: (args: CellDoubleClickedEventHandlerArgs) =>
          this.meterReadingDoubleClicked(args),
      },
      columnDefinitionService.getStringColDef({
        field: "PropertyName",
        header: "Property",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineSerialNumber",
        header: "Machine Serial Number",
        hide: true,
      }),
      {
        field: "ProgressiveDailyMachineSlotNumber",
        headerName: "Machine Slot Number(s)",
        valueGetter: (args: GridArgs) =>
          args.data.ProgressiveDailyMachineSlotNumber ??
          args.data.ProgressiveDailyReadingMachineSlotNumbers,
        filter: new FilterProvider(
          "ProgressiveDailyMachineSlotNumberOrSlotNumbers",
          $compile
        )
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineSection",
        header: "Machine Section",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineBank",
        header: "Machine Bank",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineSlot",
        header: "Machine Slot",
        hide: true,
      }),
      {
        headerName: "Machine Location(s)",
        field: "ProgressiveDailyMachineLocation",
        valueGetter: (args: GridArgs) => this.getReadingLocation(args),
        filter: new FilterProvider(
          "ProgressiveDailyMachineLocationOrLocations",
          $compile
        )
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      // columnDefinitionService.getStringColDef({
      //   field: "ProgressiveDailyMachineLocation",
      //   header: "Machine Location",
      // }),
      columnDefinitionService.getBooleanColDef({
        field: "ProgressiveDailyMachineComplete",
        header: "Machine Complete",
      }),
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDailyMachineRequiredReadings",
        header: "Machine Required Readings",
      }),
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDailyMachineReadings",
        header: "Machine Readings",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "ProgressiveDailyReadingMachineActive",
        header: "Machine Active",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyReadingMachineNotes",
        header: "Machine Notes",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "Required",
      }),
      columnDefinitionService.getNumberColDef({ field: "OptionalBelow" }),
      columnDefinitionService.getDateColDef({
        field: "ProgressiveDailyPeriodDate",
        header: "Date",
        suppressMenu: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDailyReadingReadings",
        header: "Definition Complete Readings",
      }),
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDailyReadingRequiredReadings",
        header: "Definition Required Readings",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "ProgressiveDailyReadingComplete",
        header: "Definition Complete",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyReadingNotes",
        header: "Definition Notes",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDefinitionName",
        header: "Progressive",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDefinitionGameDescription",
        header: "Game",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDefinitionGameLevelName",
        header: "Level",
      }),
      {
        field: "ProgressiveDefinitionGameLevelJackpotType",
        headerName: "Level Jackpot Type",
        filter: new FilterProvider(
          "ProgressiveDefinitionGameLevelJackpotType",
          $compile
        )
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                {
                  display: "Linked",
                  value: "Linked",
                },
                {
                  display: "Standalone",
                  value: "Standalone",
                },
              ],
              queryFieldNames: ["ProgressiveDefinitionGameLevelJackpotType"],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      },
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDefinitionGameLevelResetBase",
        header: "Level Reset Base",
        valueGetter: moneyValueGetter,
      }),
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDefinitionGameLevelCeiling",
        header: "Ceiling",
        valueGetter: (params) => {
          const ceiling = (<any>params.data!)[params.colDef!.field!];
          return ceiling ? moneyValueGetter(params) : "N/A";
        },
      }),
      columnDefinitionService.getBooleanColDef({
        field: "AtCeiling",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "Complete",
      }),
      columnDefinitionService.getStringColDef({
        field: "LastReadingUser",
      }),
      columnDefinitionService.getDateTimeColDef({
        field: "LastReadingTime",
      }),
      columnDefinitionService.getNumberColDef({
        field: "LastKnownMeterReading",
        header: "Last Period Reading",
        valueGetter: moneyValueGetter,
      }),
      columnDefinitionService.getStringColDef({
        field: "LastKnownReadingUser",
        header: "Last Period Reading User",
      }),
      columnDefinitionService.getDateTimeColDef({
        field: "LastKnownReadingTime",
        header: "Last Period Reading Time",
      }),
      columnDefinitionService.getNumberColDef({
        field: "Change",
        valueGetter: moneyValueGetterFactory({ defaultValue: 0 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "ChangePercentage",
        header: "Percent Change",
        valueGetter: percentageValueGetterFactory({
          decimalPlaces: 2,
          defaultValue: 0,
        }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "Liability",
        valueGetter: moneyValueGetter,
      }),
    ];
  }

  private readonly meterReadingDoubleClickHander =
    this.doubleClickHandlerService.patchDoubleClickHandler({
      requiresPermission: ProgressivePermissions.EditPermission,
      fetchRecord: (args) =>
        this.serverUtility.querySingleWrapped({
          url: progressiveDailyReadingGameLevelUrl,
          query: {
            Id: args.data.Id,
            ProgressiveDailyPeriodDate: args.data.ProgressiveDailyPeriodDate,
          },
        }),
      submit: (patchObject) =>
        this.serverUtility.put({
          url: progressiveDailyReadingGameLevelReadingUrl,
          updateObject: patchObject,
        }),
      patchField: (args) => this.getMeterReadingFields(args),
      getInitialPatchObject: (args) => {
        return {
          Id: args.data.ProgressiveDailyReadingGameLevelReadingIdOfRecord,
          MeterReading: args.data.LastMeterReading,
          UnableToGetReading: args.data.LastUnableToGetReading,
          ConditionNotMet: args.data.LastConditionNotMet,
        };
      },
      getRecordName: (args) => {
        const {
          ProgressiveDefinitionName: progressiveDefinition,
          ProgressiveDefinitionGameDescription: game,
          ProgressiveDefinitionGameLevelName: level,
        } = args.data;

        const location = this.getReadingLocation(args);

        return `level reading ${progressiveDefinition}/${game}/${level} at ${location}`;
      },
    });

  private getReadingLocation(args: GridArgs): string | undefined {
    return (
      args.data.ProgressiveDailyMachineLocation ??
      args.data.ProgressiveDailyReadingMachineLocations
    );
  }

  private meterReadingDoubleClicked(args: CellDoubleClickedEventHandlerArgs) {
    const complete = args.data.Complete;
    if (!complete) {
      return;
    }
    this.meterReadingDoubleClickHander(args);
  }
  private getMeterReadingFields(
    args: CellDoubleClickedEventHandlerArgs
  ): PatchFieldConfiguration[] {
    const meterReadingField: IFieldConfigurationObject = {
      key: "MeterReading",
      name: "MeterReading",
      templateOptions: {
        label: "Meter Reading",
        min: args.data.ProgressiveDefinitionGameLevelResetBase,
        type: "number",
      },
      type: "gridInput",
      watcher: {
        listener: (field, newValue, oldValue, scope) => {
          if (newValue) {
            const model = scope.model!;
            model.ConditionNotMet = false;
            model.UnableToGetReading = false;
          }
        },
      },
    };

    const conditionNotMetField: IFieldConfigurationObject = {
      key: "ConditionNotMet",
      name: "ConditionNotMet",
      templateOptions: {
        label: "Condition Not Met",
      },
      type: "gridCheckbox",
      watcher: {
        listener: (field, newValue, oldValue, scope) => {
          if (newValue) {
            const model = scope.model!;
            model.MeterReading = null;
            model.UnableToGetReading = false;
          }
        },
      },
    };

    const unableToGetReadingField: IFieldConfigurationObject = {
      key: "UnableToGetReading",
      name: "UnableToGetReading",
      templateOptions: {
        label: "Unable To Get Reading",
      },
      type: "gridCheckbox",
      watcher: {
        listener: (field, newValue, oldValue, scope) => {
          if (newValue) {
            const model = scope.model!;
            model.MeterReading = null;
            model.ConditionNotMet = false;
          }
        },
      },
    };

    let fields: PatchFieldConfiguration[] = [
      {
        formField: meterReadingField,
        referenceFieldName: "LastMeterReading",
        referenceFieldFormatters: [
          (data) =>
            Number.isFinite(data) ? `$${(<number>data).toFixed(2)}` : undefined,
        ],
      },
      {
        formField: unableToGetReadingField,
        referenceFieldName: "LastUnableToGetReading",
        referenceFieldFormatters: [(data) => (data ? "Yes" : "No")],
      },
    ];

    const data = args.data;

    if (
      data.OptionalBelow &&
      data.ProgressiveDefinitionGameLevelResetBase &&
      data.OptionalBelow >= data.ProgressiveDefinitionGameLevelResetBase
    ) {
      fields = [
        ...fields,
        {
          formField: conditionNotMetField,
          referenceFieldName: "LastConditionNotMet",
          referenceFieldFormatters: [(data) => (data ? "Yes" : "No")],
        },
      ];
    }

    return fields;
  }
}
